import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { LoadingButton as Button } from "@mui/lab";
import {
  alpha,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { useTheme } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import Rating from "components/rating/starRating";
import { useTemplate } from "containers/templateDetail/store";
import fetcher from "lib/fetcher";
import cloneDeep from "lodash/cloneDeep";
import trim from "lodash/trim";
import * as React from "react";
import { createOrderReview } from "services/dashboard";
import ImageUploader from "./imageUploader";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Image = styled("img")({
  width: "100%",
});

const ImageWrap = ({ image, onDelete }) => {
  return (
    <Grid
      item
      sx={{
        position: "relative",
        fontSize: 0,
        borderRadius: 1,
        overflow: "hidden",
        cursor: "pointer",
        "&:hover": {
          "& .backdrop": {
            display: "flex",
          },
        },
      }}
      xs={3}
      onClick={onDelete}
    >
      <Image src={`${process.env.NEXT_PUBLIC_SITE_URL}${image.src}`} />
      <Box
        className="backdrop"
        sx={{
          display: "none",
          position: "absolute",
          width: "100%",
          height: "100%",
          background: (theme) => alpha(theme.palette.common.white, 0.6),
          alignItems: "center",
          justifyContent: "center",
          top: 0,
          left: 0,
        }}
      >
        <DeleteIcon />
      </Box>
    </Grid>
  );
};

const DEFAULT_RATING = 5;

export default function WriteReview({ onSuccess }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const { id: templateID } = useTemplate();
  const [selectedOrderId, setSelectedOrderId] = React.useState(null);
  const [orders, setOrders] = React.useState([]);
  const [rating, setRating] = React.useState(DEFAULT_RATING);
  const [error, setError] = React.useState({});
  const [images, setImages] = React.useState([]);
  const [content, setContent] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  React.useEffect(() => {
    if (open) {
      setLoading(true);
      fetcher({ url: `/api/v2/templates/${templateID}/orders?not_review=1` })
        .then((res) => {
          setLoading(false);
          if (res && res.orders && res.orders.length > 0) {
            setSelectedOrderId(res.orders[0].id);
            setOrders(res.orders);
          }
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  }, [open]);

  const onFinish = (data) => {
    const arr = cloneDeep(images);
    arr.push(data);
    setImages(arr);
  };

  const onDelete = (index) => {
    const arr = cloneDeep(images);
    arr.splice(index, 1);
    setImages(arr);
  };

  const onRatingChange = (ev, value) => {
    setRating(value);
  };

  const onContentChange = (ev) => {
    setError({});
    setContent(ev.target.value);
  };

  const onSubmit = () => {
    if (trim(content).length <= 0) {
      setError({
        content: "Review content is required.",
      });
    }
    setSubmitting(true);
    createOrderReview({
      orderId: selectedOrderId,
      data: {
        score: rating,
        order_id: selectedOrderId,
        content: trim(content),
        images: images.map((item) => item.src),
      },
    })
      .then((res) => {
        setSubmitting(false);
        onSuccess && onSuccess();
        handleClose();
      })
      .catch((err) => {
        setSubmitting(false);
      });
  };

  const handleChange = (ev) => {
    setSelectedOrderId(ev.target.value);
  };

  const handleExited = () => {
    setImages([]);
    setContent("");
  };

  return (
    <React.Fragment>
      <Button
        variant="contained"
        fullWidth
        onClick={handleOpen}
        sx={{
          height: 48,
          fontSize: (theme) => theme.typography.body1.fontSize,
        }}
      >
        Write a review
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={matchesSm}
        TransitionComponent={Transition}
        TransitionProps={{
          onExited: handleExited,
        }}
      >
        <DialogTitle>Rate and Review</DialogTitle>
        {loading && (
          <DialogContent>
            <Box
              sx={{
                width: matchesSm ? "100%" : 350,
                height: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          </DialogContent>
        )}
        {!loading && orders.length === 0 && (
          <DialogContent>
            <Box sx={{ width: matchesSm ? "100%" : 350 }}>
              <Typography variant="body2">
                You have to order this product first to be able to review it
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }} onClick={handleClose}>
                Got It
              </Button>
            </Box>
          </DialogContent>
        )}
        {!loading && orders.length > 0 && (
          <DialogContent>
            <Box sx={{ width: matchesSm ? "100%" : 350 }}>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Rating
                  size={40}
                  defaultValue={DEFAULT_RATING}
                  onChange={onRatingChange}
                />
              </Box>
              <Select
                id="review-order-select"
                fullWidth
                value={selectedOrderId}
                onChange={handleChange}
              >
                {orders.map((order) => {
                  return (
                    <MenuItem key={order.id} value={order.id} sx={{ py: 2 }}>
                      Order Number:&nbsp;
                      <b>【{order.order_number}】</b>
                    </MenuItem>
                  );
                })}
              </Select>
              <TextField
                multiline
                minRows={4}
                fullWidth
                value={content}
                sx={{ mt: 2 }}
                error={!!error["content"]}
                helperText={error["content"] || null}
                onChange={onContentChange}
                placeholder="Hope you like our products."
              />
              {images.length > 0 && (
                <Grid container sx={{ mt: 2 }}>
                  {images.map((image, index) => {
                    return (
                      <ImageWrap
                        key={index}
                        image={image}
                        onDelete={() => onDelete(index)}
                      />
                    );
                  })}
                </Grid>
              )}
              <Box sx={{ display: "flex", my: 1 }}>
                <ImageUploader
                  onFinish={onFinish}
                  disabled={images.length > 3}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  variant="contained"
                  sx={{ ml: 2 }}
                  loading={isSubmitting}
                  onClick={onSubmit}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </React.Fragment>
  );
}
